import { postSurvey, getSurvey } from '@/service/survey'
import { getUserCustomSurvey } from '@/service/custom-surveys'
import { curry, pipe, path } from 'ramda'

// initial state
function initialState() {
  return {
    customSurvey: null,
    survey: [],
    error: null,
    page: {
      current: undefined,
      total: undefined,
      loading: false,
    },
  }
}

const state = initialState()

// getters
const getters = {
  customSurvey: state => state.customSurvey,
  survey: state => state.survey,
  currentQuestion: state => state.survey[state.page.current - 1] || null,
  currentAnswered: state =>
    (state.survey[state.page.current - 1] || {}).answered || {},
  surveyPage: state => state.page,
  surveyError: state => state.error,
}

// actions
const actions = {
  // survey from localStorage if exists
  async getSurvey({ commit }) {
    if (localStorage.survey) {
      commit('setSurvey', JSON.parse(localStorage.survey))
      commit('setPage', JSON.parse(localStorage.surveyPage))
    } else {
      try {
        const { survey, customSurvey = null } = await getSurvey()

        if (customSurvey) {
          // function for custom survey
          const verifySurvey = async id => {
            try {
              const {
                data: { customSurvey },
              } = await getUserCustomSurvey(id)
              return !!customSurvey
            } catch (_) {
              return false
            }
          }
          const hasCustomSurvey = await verifySurvey(customSurvey)
          if (hasCustomSurvey) commit('setCustomSurvey', customSurvey)
        }

        commit(
          'setSurvey',
          survey.map(question => ({ ...question, answered: null }))
        )
        commit('setPage', {
          current: 1,
          total: survey.length,
        })
        commit('setError', null)
      } catch (error) {
        const wrapperCommit = (key, data) => commit(key, data)
        const setError = curry(wrapperCommit)('setError')
        pipe(
          path(['response']),
          setError
        )(error)
      }
    }
  },

  // finish survey and clear local storage
  async finishSurvey({ state, commit }) {
    // avoid empty action
    if (state.survey.length === 0) {
      commit('setCustomSurvey', initialState().customSurvey)
      commit('setSurvey', initialState().survey)
      commit('setPage', initialState().page)
      delete localStorage.survey
      delete localStorage.surveyPage
      return
    }

    let response = postSurvey({ survey: state.survey })
    response.then(() => {
      commit('setCustomSurvey', initialState().customSurvey)
      commit('setSurvey', initialState().survey)
      commit('setPage', initialState().page)
      delete localStorage.survey
      delete localStorage.surveyPage
    })
    return response
  },

  // finish survey and clear local storage
  startSurvey({ commit }) {
    commit('sendSurveyToLocalStorage')
  },

  surveyPreviousPage({ commit }) {
    commit('surveyPreviousPage')
  },

  surveyNextPage({ commit }) {
    commit('surveyNextPage')
  },
}

// mutations
const mutations = {
  setCustomSurvey(state, customSurvey) {
    state.customSurvey = customSurvey
  },

  setSurvey(state, survey) {
    state.survey = survey
  },

  sendSurveyToLocalStorage(state) {
    localStorage.survey = JSON.stringify(state.survey)
    localStorage.surveyPage = JSON.stringify(state.page)
  },

  toggleSurveyPostLoading(state) {
    state.page.loading = !state.page.loading
  },

  setPage(state, page) {
    state.page = Object.assign(state.page, page)
  },

  surveyNextPage(state) {
    state.page.current++
  },

  selectAnswer(state, answer) {
    const isMulti = answer.hasOwnProperty('type') && answer.type === 'multi'

    const answered = {
      answer_id: answer.answer_id,
      ...(!isMulti && { score: answer.score }),
      text: answer.text,
      ...(answer.image && { image: answer.image }),
    }

    try {
      if (state.survey[state.page.current - 1].answers[0].feedback) {
        answered.feedback = answer.feedback
        answered.feedback_answer = answer.feedback_answer
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }

    if (isMulti) {
      const _answered = state.survey[state.page.current - 1].answered || []
      const hasAnswered =
        _answered.filter(a => a.answer_id === answer.answer_id).length > 0

      if (hasAnswered) {
        // if exist in current > remove
        const filteredAnswered = _answered.filter(
          a => a.answer_id !== answer.answer_id
        )
        state.survey[state.page.current - 1].answered =
          filteredAnswered.length > 0 ? filteredAnswered : null
      } else {
        // if not exist in current > add
        state.survey[state.page.current - 1].answered = [..._answered, answer]
      }
    } else {
      state.survey[state.page.current - 1].answered = answered
    }
  },

  selectAnswerFeedback(state, feedback) {
    state.survey[state.page.current - 1].answered.feedback_answer = feedback
  },

  surveyPreviousPage(state) {
    state.page.current--
  },

  setError(state, error) {
    state.error = error
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
